import { layout } from '@universityofmaryland/web-elements-library/composite';
import { Attributes, Lifecycle, Model } from '@universityofmaryland/web-model-library';
import { CreateComponentFunction, ComponentRegistration, LayoutProps } from '../../_types';

interface ScrollTopProps extends Pick<LayoutProps, 'isFixed'> {}

const tagName = 'umd-element-scroll-top';

const createComponent: CreateComponentFunction = (element) => {
  const props: ScrollTopProps = {
    isFixed: Attributes.isLayout.fixed({ element }),
  };

  return layout.scrollTop(props);
};

/**
 * Scroll to Top
 *
 * A utility component that provides a smooth scroll-to-top functionality.
 * Displays a button that appears after scrolling down and returns users to the top of the page.
 * Can be positioned fixed or absolute within its container.
 *
 * ## Custom Element
 * `<umd-element-scroll-top>`
 *
 * ## Attributes
 * - `data-layout-fixed` - Positioning mode:
 *   - When present - Fixed positioning relative to viewport
 *   - When absent - Absolute positioning within parent container
 *
 * @example
 * ```html
 * <!-- Basic scroll to top button -->
 * <umd-element-scroll-top></umd-element-scroll-top>
 * ```
 *
 * @example
 * ```html
 * <!-- Fixed position scroll to top -->
 * <umd-element-scroll-top data-layout-fixed></umd-element-scroll-top>
 * ```
 *
 * @example
 * ```html
 * <!-- Within a specific container -->
 * <div class="content-wrapper" style="position: relative;">
 *   <article>
 *     <!-- Long content here -->
 *   </article>
 *   <umd-element-scroll-top></umd-element-scroll-top>
 * </div>
 * ```
 *
 * @category Components
 * @since 1.0.0
 */
export const LayoutScrollTop: ComponentRegistration = Model.defineComponent({
  tagName,
  createComponent,
  afterConnect: Lifecycle.hooks.loadOnConnect,
}, { eager: false });

/** Backwards compatibility alias for grouped exports */
export { LayoutScrollTop as scrollTop };
